import React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import {Button, Icon} from '@thryvlabs/maverick'
import '../../styles/partnerScheduler.css'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

// "Friday, June 10, 2026 at 09:15 AM CST" — the same wording the Scheduling
// Step shows once a call is booked. Rendered in the zone the call was booked
// in (callDetails.timeZone), not the browser's.
export function formatScheduledDateTime(callDetails) {
  if (!callDetails?.thryvBookingDate) return ''
  const instant = dayjs(callDetails.thryvBookingDate)
  const zoned = callDetails.timeZone
    ? instant.tz(callDetails.timeZone)
    : instant
  return `${zoned.format('dddd, MMMM D, YYYY')} at ${zoned.format(
    'hh:mm A',
  )} ${zoned.format('z')}`
}

// Circular-arrow "reset" glyph — the same 16x16 SVG (and #0327A9 stroke) as
// Core OOE's core/assets/icons/reset.svg, inlined because this app has no
// shared Icon component.
const ResetIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2.66669 8C2.66669 10.9455 5.05451 13.3333 8.00002 13.3333C10.9455 13.3333 13.3334 10.9455 13.3334 8C13.3334 5.05448 10.9455 2.66667 8.00002 2.66667C6.24513 2.66667 4.68748 3.51427 3.71646 4.82224"
      stroke="#0327A9"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.33331 2V5H6.33331"
      stroke="#0327A9"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Heading for one call, e.g. "Marketing Center Kickoff Call".
export const CallHeading = ({children}) => (
  <h3 className="ps-call-title">{children}</h3>
)

// "Thank you[ <email>], your <call> is scheduled for <date/time>." strip.
// `email` and `onReset` are optional — the success page passes the email and
// the scheduler page offers the reset link.
export const ScheduledCallNotice = ({
  label,
  dateTime,
  email,
  onReset,
  resetLabel = 'Reschedule',
  disabled = false,
}) => (
  <div className="ps-notice">
    <Icon
      type="regular"
      variant="calendarClock"
      color="gray"
      height="20"
      width="20"
    />
    <p className="ps-notice-text">
      Thank you
      {email && (
        <>
          {' '}
          <b>{email}</b>
        </>
      )}
      , your <u>{label}</u> is scheduled for <b>{dateTime}</b>.
    </p>
    {onReset && (
      <button
        type="button"
        className="ps-notice-reset"
        disabled={disabled}
        onClick={onReset}
        aria-label={`${resetLabel} ${label}`}
      >
        <ResetIcon />
        {resetLabel}
      </button>
    )}
  </div>
)

// Shown above the date/slot picker while rescheduling: the appointment that is
// still booked, with the button to keep it (nothing is released until a new
// slot is booked).
export const CurrentAppointmentBanner = ({label, dateTime, onKeep}) => (
  <div className="ps-notice ps-notice--current">
    <Icon
      type="regular"
      variant="calendarClock"
      color="gray"
      height="20"
      width="20"
    />
    <p className="ps-notice-text">
      Your <u>{label}</u> is currently scheduled for <b>{dateTime}</b>.
    </p>
    <Button variant="secondary" className="ad-secondary" onClick={onKeep}>
      Keep Current Appointment
    </Button>
  </div>
)
